import styled from 'styled-components';

interface RootStyleProps {
  maxRowCount: number;
  loading: boolean;
  colCount: number;
}

interface DraggableCellStyleProps {
  isDragging: boolean;
}

interface DroppableCellStyleProps {
  isOver: boolean;
  canDrop: boolean;
}

export const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem;
  justify-content: flex-end;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

export const Root = styled.div<RootStyleProps>`
  filter: ${(props) => (props.loading ? 'blur(0.1rem)' : '')};

  overflow-y: hidden !important;
  .main {
    flex-grow: 1;
    overflow: auto;
  }
  .dragNDropZone {
    display: flex;
    /* height: 100%; */
    > div {
      padding: 1rem;
    }

    .dragZone {
      width: 16rem;
      height: calc(100vh - 8.5rem); /* 고정 높이로 변경 */
      max-height: calc(100vh - 7.5rem);
      position: sticky;
      top: 0;
      overflow-y: auto; /* 내용이 넘칠 때만 스크롤 */
      overflow-x: hidden;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      gap: 0.5rem;

      /* 스크롤바 숨기기 */
      &::-webkit-scrollbar {
        display: none; /* 스크롤바 완전히 숨김 */
      }

      /* Firefox용 스크롤바 숨기기 */
      scrollbar-width: none;
      .drag-group {
        border-radius: 0.375rem;
        background: var(--surface);
        max-height: calc(100vh - 7.5rem); /* 각 그룹의 최대 높이를 더 작게 제한 */
        display: flex;
        flex-direction: column;
        flex-shrink: 0; /* 그룹이 축소되지 않도록 */
      }
      .drag-group-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem;
        cursor: pointer;
        user-select: none;
        border-radius: 0.25rem;
        transition: background-color 0.2s ease, color 0.2s ease;
        color: var(--selected-primary);
        font-weight: var(--font-semibold);

        .expandIcon {
          font-size: 24px;
          width: 2rem;
          height: 2rem;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 0.5rem;
          color: var(--tonal-blue);
          background-color: var(--selected-primary);
        }

        &:hover {
          font-weight: var(--font-semibold);
          background-color: var(--tonal-blue);
          outline: 2px solid var(--selected-primary);
        }
      }
      .drag-group-body {
        overflow: hidden; /* 아코디언 효과를 위해 hidden으로 변경 */
        transition: max-height 0.25s ease-out, opacity 0.2s ease-out, margin-top 0.25s ease-out;
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        max-height: 0; /* 기본적으로 닫힌 상태 */
        opacity: 0;
        margin-top: 0; /* 닫힌 상태에서는 margin-top 없음 */
        padding-left: 1.25rem;

        /* 스크롤바 숨기기 */
        &::-webkit-scrollbar {
          display: none; /* 스크롤바 완전히 숨김 */
        }

        /* Firefox용 스크롤바 숨기기 */
        scrollbar-width: none;

        /* 열린 상태일 때 */
        &.expanded {
          max-height: 360px;
          opacity: 1;
          overflow-y: auto; /* 열린 상태에서만 스크롤 허용 */
          margin-top: 0.5rem; /* 헤더와의 간격 확보 */
        }

        > div {
          gap: 0.25rem;
          border-radius: 0.25rem;
          justify-content: flex-start;
          line-height: 1.125rem;
          word-break: keep-all;
          padding: 0.5rem 0.75rem;
          font-weight: 400;
        }
      }
      .arrow {
        display: inline-block;
        transition: transform 0.2s ease;
      }
      .arrow.collapsed {
        transform: rotate(-90deg);
      }
    }

    .dropZone {
      display: grid;
      grid-template-columns: repeat(${(props) => (props.colCount ? props.colCount : 4)}, 1fr);
      grid-template-rows: repeat(${(props) => (props.maxRowCount <= 8 ? 8 : props.maxRowCount)}, 1fr);
      gap: 0.125rem;
      flex-grow: 1;
      > div {
        margin: 0;
        border-radius: 0.25rem;
        overflow: hidden;
        position: relative;
        > div:nth-child(1) {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          height: 2.5rem;
          position: absolute;
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    .dragNDropZone .dropZone {
      grid-template-columns: repeat(1, 1fr);
    }
  }
`;

export const DraggableCellStyle = styled.div<DraggableCellStyleProps>`
  color: var(--text-primary);
  background-color: var(--tonal);
  font-size: 0.875rem;
  padding: 0.5rem;
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: ${(props) => (props.isDragging ? '0.5' : '1')};
`;

export const DroppableCellStyle = styled.div<DroppableCellStyleProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  background-color: ${(props) => (props.isOver && props.canDrop ? 'red' : '#5ac8fa')};
  height: 100%;
  border-radius: 0.25rem;
  .dropCell {
    width: 100%;
    flex-grow: 1;
    div {
      word-break: keep-all;
      text-align: center;
      line-height: 1.25;
      max-height: 6rem;
      min-height: 100%;
      padding-top: 2.5rem;
      padding-bottom: 2.5rem;
    }
  }
  .closeIcon {
    font-size: 24px;
    color: var(--text-secondary);
    &:hover {
      color: var(--text-primary);
    }
  }
`;
